import * as React from "react";
import { useState } from "react";
import axios from "axios";
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Typography,
  Box,
  IconButton,
  Collapse,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useNotify } from "react-admin";
import ProviderEmailDialog from "../components/ProviderEmailDialog";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet } from "../getConfig";
import { CancellationResultCard } from "../shared/CancellationResultCard";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: theme.spacing(2),
  },
  card: {
    minWidth: 300,
    marginBottom: theme.spacing(2),
    width: "100%",
    maxWidth: "1400px",
  },
  form: {
    padding: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
  },
  input: {
    flex: 1,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  table: {
    minWidth: 800,
  },
  tableHead: {
    backgroundColor: theme.palette.grey[100],
  },
  tableCell: {
    padding: theme.spacing(1),
    fontSize: "0.875rem",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  noDataContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
    color: theme.palette.grey[500],
  },
  expandedContent: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
  },
}));

/**
 * Derive a single display_category + display_status for a voucher (all records share the same values).
 * Mirrors the backend voucher_state SQL logic.
 */
function deriveDisplayValues(records: any[]): any[] {
  if (records.length === 0) return records;

  const emailActions = ['emailed_to_tillo', 'fallback_email_to_tillo'];
  const userRefunded = records.some((r: any) => ['only_cancel_to_user', 'cancelled_by_tillo_api'].includes(r.action) && r.status === 'completed');
  const refundFailed = records.some((r: any) => r.status === 'cancel_to_user_failed');
  const tilloConfirmedEmail = records.some((r: any) => emailActions.includes(r.action) && r.status === 'completed');
  const tilloConfirmedApi = records.some((r: any) => r.action === 'cancelled_by_tillo_api' && ['completed', 'tillo_completed', 'cancel_to_user_failed'].includes(r.status));
  const emailPending = records.some((r: any) => emailActions.includes(r.action) && r.status === 'pending');
  const emailDeclined = records.some((r: any) => emailActions.includes(r.action) && r.status === 'declined');

  const tilloConfirmed = tilloConfirmedEmail || tilloConfirmedApi;

  let display_category: string;
  if ((userRefunded || refundFailed) && tilloConfirmed) display_category = 'cancelled_via_tillo';
  else if (userRefunded || refundFailed) display_category = 'loss_absorbed';
  else if (tilloConfirmed) display_category = 'cancelled_via_tillo';
  else if (emailPending) display_category = 'emailed_to_tillo';
  else if (emailDeclined) display_category = 'emailed_to_tillo';
  else display_category = 'other';

  let display_status: string;
  if ((userRefunded || refundFailed) && emailPending) display_status = 'pending';
  else if (userRefunded) display_status = 'completed';
  else if (refundFailed) display_status = 'refund_failed';
  else if (tilloConfirmed) display_status = 'pending';
  else if (emailPending) display_status = 'pending';
  else if (emailDeclined) display_status = 'declined';
  else display_status = 'pending';

  return records.map((r: any) => ({ ...r, display_category, display_status }));
}

const ACTION_LABELS: Record<string, string> = {
  cancelled_by_tillo_api: "Cancelled by Tillo API",
  emailed_to_tillo: "Emailed to Tillo",
  fallback_email_to_tillo: "Fallback Email to Tillo",
  only_cancel_to_user: "Only Cancel to User",
};

const RAW_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  completed: "Completed",
  tillo_failed: "Tillo Failed",
  tillo_completed: "Tillo Completed",
  cancel_to_user_failed: "Cancel to User Failed",
  declined: "Declined",
  refund_failed: "Refund Failed",
};

const VoucherRow = ({ voucher, email, notify }: { voucher: any; email: string; notify: any }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [onlyCancelToUser, setOnlyCancelToUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);

  const rawHistory = voucher.cancellation_history || [];
  const cancellationHistory = deriveDisplayValues(rawHistory);
  const hasCancellationHistory = cancellationHistory.length > 0;
  
  const canExpand = voucher.status === "success" || voucher.status === "cancelled";
  const canCancel = voucher.status === "success";
  const supportsBalanceCheck = !!voucher.balance_check_supported;

  const [storeTilloLoading, setStoreTilloLoading] = useState(false);
  const [storeTilloDone, setStoreTilloDone] = useState(false);

  const handleStoreTilloCancellation = async () => {
    setStoreTilloLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken") || "";
      const response = await axios.patch<any>(
        ENDPOINTS.CANCEL_GIFT_CARD,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          params: {
            email: email,
            client_reference_id: voucher.client_reference_id,
            store_tillo_cancellation: true,
          },
        }
      );
      setResult(response.data);
      setStoreTilloDone(true);
      if (response.data.success) {
        notify(response.data.message || "Tillo cancellation stored", "success");
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error.message || "Failed to store Tillo cancellation";
      setResult({ success: false, message: errorMessage });
      setStoreTilloDone(true);
    } finally {
      setStoreTilloLoading(false);
    }
  };

  const [fallbackDialogOpen, setFallbackDialogOpen] = useState(false);
  const [fallbackLoading, setFallbackLoading] = useState(false);
  const [fallbackBody, setFallbackBody] = useState("");
  const [fallbackDone, setFallbackDone] = useState(false);

  const [emailToTilloDialogOpen, setEmailToTilloDialogOpen] = useState(false);
  const [emailToTilloLoading, setEmailToTilloLoading] = useState(false);
  const [emailToTilloReason, setEmailToTilloReason] = useState("");
  const [emailToTilloDone, setEmailToTilloDone] = useState(false);
  const [emailToTilloExceptionType, setEmailToTilloExceptionType] = useState<string | null>(null);
  const [emailBodyBefore, setEmailBodyBefore] = useState("");
  const [emailBodyAfter, setEmailBodyAfter] = useState("");

  const tilloApiJustFailed = result?.data?.action === "cancelled_by_tillo_api" && result?.data?.status === "tillo_failed";
  const hasTilloApiFailed = tilloApiJustFailed || cancellationHistory.some(
    (r: any) => r.action === "cancelled_by_tillo_api" && r.status === "tillo_failed"
  );
  const hasFallbackAlreadySent = cancellationHistory.some(
    (r: any) => r.action === "fallback_email_to_tillo"
  );
  const canSendFallbackEmail = hasTilloApiFailed && !hasFallbackAlreadySent && !fallbackDone;

  const getDefaultEmailBody = () => {
    const clientRefId = voucher.client_reference_id || "";
    return `A user has reported that the e-gift card referenced below did not work as expected.\n\nPlease can you assist with cancelling this voucher. If cancellation is not possible, please confirm whether the voucher was successfully loaded and activated.\n\nIn the event that the voucher has already been redeemed, please share the full redemption history along with the location details.\n\nClient Reference Id: ${clientRefId}`;
  };

  const openFallbackDialog = () => {
    setFallbackBody(getDefaultEmailBody());
    setFallbackDone(false);
    setResult(null);
    setFallbackDialogOpen(true);
  };

  const closeFallbackDialog = () => {
    setFallbackDialogOpen(false);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return dateString.replace(/\.\d{3}Z$/, "").replace("T", " ");
  };

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(numAmount);
  };

  const handleCancel = async () => {
    setLoading(true);
    setResult(null);

    try {
      const accessToken = localStorage.getItem("accessToken") || "";
      const response = await axios.patch<any>(
        ENDPOINTS.CANCEL_GIFT_CARD,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          params: {
            email: email,
            client_reference_id: voucher.client_reference_id,
            only_cancel_to_user: onlyCancelToUser,
          },
        }
      );

      console.log("Cancellation response:", JSON.stringify(response.data, null, 2));

      if (response.data.requires_email_to_tillo) {
        setEmailToTilloReason("");
        setEmailToTilloDone(false);
        setEmailToTilloExceptionType(response.data.data?.exception_type || null);
        setEmailBodyBefore(response.data.email_body_before || "");
        setEmailBodyAfter(response.data.email_body_after || "");
        setEmailToTilloDialogOpen(true);
      } else {
        setResult(response.data);
        if (response.data.success) {
          notify(response.data.message || "Voucher cancelled successfully", "success");
        }
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error.message || "Failed to cancel voucher";
      setResult({ success: false, message: errorMessage });
      // Error is displayed in the expanded section, no need for snackbar
    } finally {
      setLoading(false);
    }
  };

  const handleFallbackEmail = async () => {
    setFallbackLoading(true);

    try {
      const accessToken = localStorage.getItem("accessToken") || "";
      const response = await axios.patch<any>(
        ENDPOINTS.CANCEL_GIFT_CARD,
        {
          custom_email_body: fallbackBody,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          params: {
            email: email,
            client_reference_id: voucher.client_reference_id,
            fallback_email_to_tillo: true,
          },
        }
      );

      setResult(response.data);
      setFallbackDone(true);
      if (response.data.success) {
        notify(response.data.message || "Fallback email sent to Tillo", "success");
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error.message || "Failed to send fallback email";
      setResult({ success: false, message: errorMessage });
      setFallbackDone(true);
    } finally {
      setFallbackLoading(false);
    }
  };

  const handleEmailToTillo = async () => {
    setEmailToTilloLoading(true);

    try {
      const reasonLine = emailToTilloReason.trim() ? `\n\nReason: ${emailToTilloReason.trim()}` : "";
      const fullBody = `${emailBodyBefore}${reasonLine}\n\n${emailBodyAfter}`;

      const accessToken = localStorage.getItem("accessToken") || "";
      const response = await axios.patch<any>(
        ENDPOINTS.CANCEL_GIFT_CARD,
        {
          custom_email_body: fullBody,
          cancellation_reason: emailToTilloReason.trim() || undefined,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          params: {
            email: email,
            client_reference_id: voucher.client_reference_id,
          },
        }
      );

      setResult(response.data);
      setEmailToTilloDialogOpen(false);
      if (response.data.success) {
        notify(response.data.message || "Email sent to Tillo", "success");
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error.message || "Failed to send email to Tillo";
      setResult({ success: false, message: errorMessage });
      setEmailToTilloDialogOpen(false);
    } finally {
      setEmailToTilloLoading(false);
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>
          {canExpand ? (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          ) : (
            <Box style={{ width: 40 }} />
          )}
        </TableCell>
        <TableCell className={classes.tableCell}>{voucher.id}</TableCell>
        <TableCell className={classes.tableCell}>{voucher.brand || "N/A"}</TableCell>
        <TableCell className={classes.tableCell}>{formatCurrency(voucher.amount)}</TableCell>
        <TableCell className={classes.tableCell}>{voucher.status || "N/A"}</TableCell>
        <TableCell
          className={classes.tableCell}
          title={voucher.client_reference_id}
          style={{ cursor: "pointer", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" as const }}
          onClick={() => {
            navigator.clipboard.writeText(voucher.client_reference_id || "");
            notify("Client Reference ID copied", "info");
          }}
        >
          {voucher.client_reference_id || "N/A"}
        </TableCell>
        <TableCell className={classes.tableCell}>{formatDate(voucher.createdAt || "")}</TableCell>
      </TableRow>
      {canExpand && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box className={classes.expandedContent}>
                {supportsBalanceCheck && (
                  <Box style={{
                    marginBottom: 20,
                    padding: "12px 16px",
                    backgroundColor: "#e3f2fd",
                    borderLeft: "4px solid #1976d2",
                    borderRadius: 4,
                  }}>
                    <Typography variant="subtitle2" style={{ color: "#0d47a1", fontWeight: 700, marginBottom: 4 }}>
                      This brand supports live balance check
                    </Typography>
                    <Typography variant="body2" style={{ color: "#1565c0", lineHeight: 1.6 }}>
                      Please verify the card balance before proceeding with cancellation.
                      {voucher.url ? (
                        <>
                          {" "}
                          <a href={voucher.url} target="_blank" rel="noopener noreferrer" style={{ color: "#1565c0", fontWeight: 600 }}>
                            Check Balance Here
                          </a>
                        </>
                      ) : null}
                    </Typography>
                    <Typography variant="body2" style={{ color: "#bf360c", fontWeight: 600, marginTop: 8, lineHeight: 1.6 }}>
                      Do not click "Cancel Voucher" if the voucher has been redeemed — it will trigger a cancellation request and generate an unnecessary email to Tillo.
                    </Typography>
                  </Box>
                )}

                {/* Cancellation History Section */}
                {hasCancellationHistory && (
                  <Box style={{ marginBottom: 24 }}>
                    <Typography variant="h6" gutterBottom style={{ color: "#d32f2f", fontWeight: 600 }}>
                      <span role="img" aria-label="clipboard">📋</span> Cancellation History
                    </Typography>
                    <TableContainer component={Paper} style={{ marginTop: 8, border: "2px solid #e0e0e0" }}>
                      <Table size="small">
                        <TableHead style={{ backgroundColor: "#f5f5f5" }}>
                          <TableRow>
                            <TableCell style={{ fontWeight: 700 }}>Reference</TableCell>
                            <TableCell style={{ fontWeight: 700 }}>Action</TableCell>
                            <TableCell style={{ fontWeight: 700 }}>Status</TableCell>
                            <TableCell style={{ fontWeight: 700 }}>Assignee</TableCell>
                            <TableCell style={{ fontWeight: 700 }}>Created At</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cancellationHistory.map((record: any, index: number) => (
                            <TableRow key={record.id || index}>
                              <TableCell>{record.reference || "N/A"}</TableCell>
                              <TableCell>{ACTION_LABELS[record.action] || record.action || "N/A"}</TableCell>
                              <TableCell>
                                <span
                                  style={{
                                    color: record.status === "completed" || record.status === "tillo_completed" ? "#28a745"
                                      : record.status === "pending" ? "#856404"
                                      : ["declined", "refund_failed", "tillo_failed", "cancel_to_user_failed"].includes(record.status) ? "#dc3545"
                                      : "#6c757d",
                                    fontWeight: 600
                                  }}
                                >
                                  {RAW_STATUS_LABELS[record.status] || record.status || "N/A"}
                                </span>
                              </TableCell>
                              <TableCell>{record.assignee || "N/A"}</TableCell>
                              <TableCell>{formatDate(record.createdAt || "")}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}

                {canSendFallbackEmail && (
                  <Box style={{
                    marginBottom: 20,
                    padding: "12px 16px",
                    backgroundColor: "#fff8e1",
                    borderLeft: "4px solid #ff9800",
                    borderRadius: 4,
                  }}>
                    <Typography variant="subtitle2" style={{ color: "#e65100", fontWeight: 700, marginBottom: 4 }}>
                      Tillo API cancellation failed
                    </Typography>
                    <Typography variant="body2" style={{ color: "#bf360c", marginBottom: 12, lineHeight: 1.6 }}>
                      You can send a fallback email to Tillo for manual cancellation.
                      This will create a new record with a SPR reference and notify Tillo via email.
                    </Typography>
                    <Button
                      variant="contained"
                      style={{ backgroundColor: "#ff9800", color: "#fff" }}
                      onClick={openFallbackDialog}
                      disabled={fallbackLoading || loading || fallbackDone}
                    >
                      Send Fallback Email to Tillo
                    </Button>
                  </Box>
                )}

                {canCancel ? (
                  <>
                    <Typography variant="h6" gutterBottom>
                      Cancel Voucher
                    </Typography>

                    <Box style={{ marginBottom: 16 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={onlyCancelToUser}
                            onChange={(e) => setOnlyCancelToUser(e.target.checked)}
                            disabled={loading}
                          />
                        }
                        label="Only Cancel to User (Skip Tillo cancellation)"
                      />
                    </Box>

                    <Box style={{ display: "flex", gap: 12, alignItems: "center" }}>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={handleCancel}
                        disabled={loading || fallbackLoading || storeTilloLoading}
                        startIcon={loading ? <CircularProgress size={20} /> : null}
                      >
                        {loading ? "Cancelling..." : "Cancel Voucher"}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleStoreTilloCancellation}
                        disabled={loading || fallbackLoading || storeTilloLoading || storeTilloDone}
                        startIcon={storeTilloLoading ? <CircularProgress size={20} /> : null}
                      >
                        {storeTilloLoading ? "Storing..." : "Store Tillo Cancellation Only (No Real Action)"}
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Box
                    style={{
                      padding: 16,
                      backgroundColor: "#fff3cd",
                      border: "1px solid #ffc107",
                      borderRadius: 4,
                    }}
                  >
                    <Typography variant="body1" style={{ color: "#856404" }}>
                      This voucher has already been cancelled. No further action can be taken.
                    </Typography>
                  </Box>
                )}

                {result && <CancellationResultCard result={result} />}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}

      <Dialog open={fallbackDialogOpen} onClose={closeFallbackDialog} maxWidth="md" fullWidth>
        <DialogTitle style={{ backgroundColor: "#fff8e1", borderBottom: "1px solid #ffe082" }}>
          <Typography variant="h6" style={{ color: "#e65100", fontWeight: 700 }}>
            Send Fallback Email to Tillo
          </Typography>
          <Typography variant="body2" style={{ color: "#bf360c", marginTop: 4 }}>
            SPR reference will be auto-prepended to the subject. Edit the content below before sending.
          </Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 20 }}>
          {!fallbackDone ? (
            <TextField
              label="Email Body"
              value={fallbackBody}
              onChange={(e) => {
                if (e.target.value.length <= 1000) setFallbackBody(e.target.value);
              }}
              variant="outlined"
              fullWidth
              multiline
              rows={14}
              helperText={`${fallbackBody.length}/1000 characters — Hi Team, Thanks {name}, and subject are added automatically`}
              error={fallbackBody.length >= 1000}
            />
          ) : (
            result && <CancellationResultCard result={result} />
          )}
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px" }}>
          {!fallbackDone ? (
            <>
              <Button onClick={closeFallbackDialog} disabled={fallbackLoading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                style={{ backgroundColor: "#ff9800", color: "#fff" }}
                onClick={handleFallbackEmail}
                disabled={fallbackLoading || !fallbackBody.trim()}
                startIcon={fallbackLoading ? <CircularProgress size={20} /> : null}
              >
                {fallbackLoading ? "Sending..." : "Send Email"}
              </Button>
            </>
          ) : (
            <Button onClick={closeFallbackDialog} color="primary" variant="contained">
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ProviderEmailDialog
        open={emailToTilloDialogOpen}
        onClose={() => setEmailToTilloDialogOpen(false)}
        title="Email to Tillo"
        subtitle="SPR reference and subject are added automatically. Add the cancellation reason in the highlighted field below."
        bodyBefore={emailBodyBefore}
        onBodyBeforeChange={setEmailBodyBefore}
        bodyAfter={emailBodyAfter}
        onBodyAfterChange={setEmailBodyAfter}
        reason={emailToTilloReason}
        onReasonChange={setEmailToTilloReason}
        reasonLabel={emailToTilloExceptionType === "greylist" ? "Reason (Required - min 10 chars)" : "Reason (Optional)"}
        reasonPlaceholder="Enter the reason for cancellation..."
        reasonHelperText={
          emailToTilloExceptionType === "greylist" && emailToTilloReason.trim().length < 10
            ? `${emailToTilloReason.trim().length}/10 characters minimum`
            : `${emailToTilloReason.length}/500`
        }
        reasonError={emailToTilloExceptionType === "greylist" && emailToTilloReason.trim().length > 0 && emailToTilloReason.trim().length < 10}
        reasonHighlight={emailToTilloExceptionType === "greylist"}
        loading={emailToTilloLoading}
        sendDisabled={emailToTilloExceptionType === "greylist" && emailToTilloReason.trim().length < 10}
        onSend={handleEmailToTillo}
        done={emailToTilloDone}
        doneContent={result && <CancellationResultCard result={result} />}
      />
    </>
  );
};

export const CancelVoucherList = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [email, setEmail] = useState("");
  const [vouchers, setVouchers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentSkip, setCurrentSkip] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(false);

  const LIMIT = 10;

  const fetchVouchers = async (skip: number = 0, isLoadMore: boolean = false) => {
    if (!email.trim()) {
      notify("Please enter an email address", "error");
      return;
    }

    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setVouchers([]);
    }
    setError(null);

    try {
      const config = getConfigGet({ 
        email: email.trim(),
        $limit: LIMIT,
        $skip: skip
      });
      const response = await axios.get<any>(ENDPOINTS.CANCEL_GIFT_CARD, config);

      const data = response.data.data || [];
      const total = response.data.meta?.total || 0;

      if (isLoadMore) {
        setVouchers(prev => [...prev, ...data]);
      } else {
        setVouchers(data);
      }

      setTotalCount(total);
      setCurrentSkip(skip);
      setHasSearched(true);

      // Check if there's more data to load
      const totalLoaded = isLoadMore ? (vouchers.length + data.length) : data.length;
      setHasMoreData(totalLoaded < total);

      if (data.length === 0 && !isLoadMore) {
        notify("No vouchers found for this email", "info");
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err.message || "Failed to fetch vouchers";
      setError(errorMessage);
      notify(errorMessage, "error");
      if (!isLoadMore) {
        setVouchers([]);
      }
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleSearch = () => {
    setCurrentSkip(0);
    setHasMoreData(false);
    fetchVouchers(0, false);
  };

  const handleLoadMore = () => {
    const newSkip = currentSkip + LIMIT;
    fetchVouchers(newSkip, true);
  };

  return (
    <div className={classes.main}>
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h5" component="h1" gutterBottom>
            Cancel Voucher
          </Typography>

          <div className={classes.form}>
            <TextField
              className={classes.input}
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              variant="outlined"
              size="small"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={loading || !email.trim()}
              startIcon={loading && <CircularProgress size={20} />}
            >
              Search
            </Button>
          </div>

          {error && (
            <Typography
              color="error"
              style={{ marginTop: 16, padding: 16, backgroundColor: "#ffebee", borderRadius: 4 }}
            >
              {error}
            </Typography>
          )}

          {loading && (
            <div className={classes.loadingContainer}>
              <CircularProgress />
              <Typography variant="body2" style={{ marginTop: 16 }}>
                Loading vouchers...
              </Typography>
            </div>
          )}

          {hasSearched && !loading && vouchers.length === 0 && (
            <div className={classes.noDataContainer}>
              <Typography variant="h6">No vouchers found</Typography>
            </div>
          )}

          {vouchers.length > 0 && (
            <>
              <Typography variant="h6" style={{ marginTop: 16, marginBottom: 8 }}>
                Showing {vouchers.length} of {totalCount} voucher(s)
              </Typography>

              <TableContainer component={Paper} className={classes.tableContainer}>
                <Table className={classes.table} stickyHeader>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell />
                      <TableCell className={classes.tableCell}>ID</TableCell>
                      <TableCell className={classes.tableCell}>Brand</TableCell>
                      <TableCell className={classes.tableCell}>Amount</TableCell>
                      <TableCell className={classes.tableCell}>Status</TableCell>
                      <TableCell className={classes.tableCell}>Client Reference ID</TableCell>
                      <TableCell className={classes.tableCell}>Created Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vouchers.map((voucher) => (
                      <VoucherRow key={voucher.id} voucher={voucher} email={email} notify={notify} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Load More Button */}
              {hasMoreData && (
                <Box style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    startIcon={loadingMore ? <CircularProgress size={20} /> : null}
                  >
                    {loadingMore ? "Loading..." : "Load More"}
                  </Button>
                </Box>
              )}

              {/* Results Summary */}
              {hasSearched && !loading && (
                <Box style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                  <Typography variant="body2" color="textSecondary">
                    Showing {vouchers.length} of {totalCount} vouchers
                  </Typography>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CancelVoucherList;
